import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { createPortal } from "react-dom";
import { ArrowLeft, Loader2, Lock, Mail, Sparkles, X } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { popOverlay, pushOverlay } from "@/lib/overlay-store";
import logo from "@/assets/quantum-mind-brain.png.asset.json";

type PendingAction = (() => void) | null;

type AuthGateState = {
  /** True when any account (including a legacy guest session) is signed in. */
  isAuthenticated: boolean;
  /** True for legacy anonymous "continue as guest" sessions. */
  isGuestAccount: boolean;
  /**
   * Returns true when the user may proceed. Otherwise opens the sign-up modal
   * and replays `action` automatically once the account is created.
   */
  requireAuth: (action?: () => void) => boolean;
  openAuthModal: (action?: () => void) => void;
  closeAuthModal: () => void;
};

const AuthGateContext = createContext<AuthGateState | null>(null);

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" aria-hidden>
      <path fill="#EA4335" d="M12 10.2v3.9h5.5a4.7 4.7 0 0 1-2 3.1l3.2 2.5c1.9-1.7 3-4.3 3-7.3 0-.7-.1-1.4-.2-2H12Z" />
      <path fill="#34A853" d="M6.6 14.3 5.9 15l-2.5 2A9 9 0 0 0 12 21c2.4 0 4.5-.8 6-2.2l-3.2-2.5c-.8.6-1.9.9-2.8.9-2.3 0-4.3-1.5-5-3.7l-.4-.2Z" />
      <path fill="#4A90E2" d="M3.4 7A9 9 0 0 0 3 12c0 1.8.4 3.5 1.2 5l3.4-2.6a5.4 5.4 0 0 1 0-3.4L3.4 7Z" />
      <path fill="#FBBC05" d="M12 6.6c1.3 0 2.5.5 3.4 1.3l2.6-2.6A9 9 0 0 0 3.4 7l3.2 2.6C7.4 8 9.5 6.6 12 6.6Z" />
    </svg>
  );
}

const BENEFITS = [
  "Save your progress",
  "Sync across all devices",
  "Never lose your schedules",
  "Unlock premium features",
];

export function AuthGateProvider({ children }: { children: ReactNode }) {
  const { user, loading, isAnonymous } = useAuth();
  const [open, setOpen] = useState(false);
  const pending = useRef<PendingAction>(null);
  // Existing anonymous "guest" sessions already own real saved data, so they
  // keep write access — only visitors with no session at all are prompted.
  const isAuthenticated = Boolean(user);

  const openAuthModal = useCallback((action?: () => void) => {
    pending.current = action ?? null;
    setOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    pending.current = null;
    setOpen(false);
  }, []);

  const requireAuth = useCallback(
    (action?: () => void) => {
      if (loading) return false;
      if (isAuthenticated) return true;
      openAuthModal(action);
      return false;
    },
    [loading, isAuthenticated, openAuthModal],
  );

  // Continue whatever the visitor was trying to do, as soon as they're in.
  useEffect(() => {
    if (!isAuthenticated) return;
    setOpen(false);
    const action = pending.current;
    pending.current = null;
    if (action) {
      const id = window.setTimeout(action, 60);
      return () => window.clearTimeout(id);
    }
  }, [isAuthenticated]);

  const value = useMemo<AuthGateState>(
    () => ({
      isAuthenticated,
      isGuestAccount: isAnonymous,
      requireAuth,
      openAuthModal,
      closeAuthModal,
    }),
    [isAuthenticated, isAnonymous, requireAuth, openAuthModal, closeAuthModal],
  );

  return (
    <AuthGateContext.Provider value={value}>
      {children}
      <AuthModal open={open} onClose={closeAuthModal} />
    </AuthGateContext.Provider>
  );
}

export function useAuthGate(): AuthGateState {
  const ctx = useContext(AuthGateContext);
  if (!ctx) throw new Error("useAuthGate must be used inside AuthGateProvider");
  return ctx;
}

/** Safe variant for modules that may render outside the provider. */
export function useOptionalAuthGate(): AuthGateState | null {
  return useContext(AuthGateContext);
}

function AuthModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, sendPasswordReset } = useAuth();
  const [mounted, setMounted] = useState(false);
  const [view, setView] = useState<"choice" | "email" | "signin" | "forgot">("choice");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState<"google" | "email" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    pushOverlay();
    const body = document.body;
    const previous = body.style.overflow;
    body.style.overflow = "hidden";
    return () => {
      popOverlay();
      body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (open) return;
    setView("choice");
    setError(null);
    setNotice(null);
    setPassword("");
  }, [open]);

  const google = async () => {
    setError(null);
    setNotice(null);
    setBusy("google");
    const result = await signInWithGoogle();
    if (!result.ok && !result.cancelled) setError(result.message ?? null);
    setBusy(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!email.trim()) return setError("Enter your email address.");
    if (view === "email") {
      if (password.length < 8)
        return setError("Password must be at least 8 characters.");
      if (!/[a-zA-Z]/.test(password) || !/\d/.test(password))
        return setError("Use at least one letter and one number in your password.");
      if (/^(password|12345678|qwerty|abc123)/i.test(password))
        return setError("That password is too easy to guess — try a longer, unique one.");
    } else if (view === "signin" && password.length < 6) {
      return setError("Password must be at least 6 characters.");
    }
    setBusy("email");
    const result =
      view === "email"
        ? await signUpWithEmail(email.trim(), password)
        : view === "signin"
          ? await signInWithEmail(email.trim(), password)
          : await sendPasswordReset(email.trim());
    if (!result.ok) setError(result.message ?? null);
    else if (result.message) setNotice(result.message);
    setBusy(null);
  };

  const content = (
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-5">
          <motion.button
            type="button"
            aria-label="Close"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Create your free account"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="glass relative max-h-[92dvh] w-full max-w-sm overflow-y-auto rounded-[2rem] p-7 text-center"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-4 top-4 inline-flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="size-4" />
            </button>

            <img
              src={logo.url}
              alt=""
              width={52}
              height={52}
              className="mx-auto size-13 object-contain drop-shadow-[0_0_26px_rgba(120,90,255,0.45)]"
            />

            {view === "choice" ? (
              <>
                <h2 className="mt-4 text-lg font-semibold tracking-tight">
                  Create your free account
                </h2>
                <p className="mt-1.5 text-sm text-muted-foreground">Create a free account to:</p>
                <ul className="mx-auto mt-4 space-y-2 text-left text-sm">
                  {BENEFITS.map((b) => (
                    <li key={b} className="flex items-center gap-2.5">
                      <Sparkles className="size-4 shrink-0 text-primary" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 space-y-3">
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={google}
                    className="flex h-14 w-full items-center justify-center gap-3 rounded-2xl bg-foreground text-base font-semibold text-background transition-transform active:scale-[0.97] disabled:opacity-70"
                  >
                    {busy === "google" ? (
                      <Loader2 className="size-5 animate-spin" />
                    ) : (
                      <>
                        <GoogleMark />
                        Continue with Google
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => {
                      setError(null);
                      setNotice(null);
                      setView("email");
                    }}
                    className="glass flex h-14 w-full items-center justify-center gap-3 rounded-2xl text-base font-semibold transition-transform active:scale-[0.97] disabled:opacity-70"
                  >
                    <Mail className="size-5" />
                    Continue with Email
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setView("signin");
                  }}
                  className="mt-5 inline-flex min-h-[44px] items-center text-xs text-muted-foreground underline-offset-4 hover:underline"
                >
                  Already have an account? Sign in
                </button>
              </>
            ) : (
              <>
                <h2 className="mt-4 text-lg font-semibold tracking-tight">
                  {view === "email"
                    ? "Create your free account"
                    : view === "signin"
                      ? "Welcome back"
                      : "Reset password"}
                </h2>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  {view === "forgot"
                    ? "We'll email you a secure link to set a new password."
                    : "Your progress stays saved and synced on every device."}
                </p>

                <form onSubmit={submit} className="mt-5 space-y-3 text-left">
                  <label className="glass flex items-center gap-3 rounded-2xl px-4 py-3.5">
                    <Mail className="size-4 shrink-0 text-muted-foreground" />
                    <input
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    />
                  </label>

                  {view !== "forgot" ? (
                    <label className="glass flex items-center gap-3 rounded-2xl px-4 py-3.5">
                      <Lock className="size-4 shrink-0 text-muted-foreground" />
                      <input
                        type="password"
                        autoComplete={view === "email" ? "new-password" : "current-password"}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                      />
                    </label>
                  ) : null}

                  {view === "email" ? (
                    <p className="px-1 text-[11px] leading-relaxed text-muted-foreground">
                      Use 8+ characters with at least one letter and one number. Avoid common
                      passwords — they get rejected for your safety.
                    </p>
                  ) : null}

                  <button
                    type="submit"
                    disabled={busy !== null}
                    className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-primary text-base font-semibold text-primary-foreground transition-transform active:scale-[0.97] disabled:opacity-70"
                  >
                    {busy === "email" ? (
                      <Loader2 className="size-5 animate-spin" />
                    ) : view === "email" ? (
                      "Create free account"
                    ) : view === "signin" ? (
                      "Sign in"
                    ) : (
                      "Send reset link"
                    )}
                  </button>
                </form>

                <div className="mt-4 flex items-center justify-between text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setNotice(null);
                      setView("choice");
                    }}
                    className="inline-flex min-h-[44px] items-center gap-1.5 text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="size-3.5" />
                    Back
                  </button>
                  {view === "signin" ? (
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setView("forgot");
                      }}
                      className="inline-flex min-h-[44px] items-center text-muted-foreground underline-offset-4 hover:underline"
                    >
                      Forgot password?
                    </button>
                  ) : view === "email" ? (
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setView("signin");
                      }}
                      className="inline-flex min-h-[44px] items-center font-semibold text-primary"
                    >
                      Sign in instead
                    </button>
                  ) : null}
                </div>
              </>
            )}

            {error ? (
              <p className="mt-4 text-sm text-destructive">{error}</p>
            ) : notice ? (
              <p className="mt-4 text-sm text-primary">{notice}</p>
            ) : (
              <p className="mt-4 text-[11px] text-muted-foreground">
                Free forever. No card required.
              </p>
            )}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );

  if (!mounted) return null;
  return createPortal(content, document.body);
}
